import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaSearch, FaExclamationTriangle } from 'react-icons/fa';

import Loading from '../../components/Loading';
import usePermissions from '../../components/Menu/MenuItems/hooks/usePermissions';
import { notifyApiError } from '../../utils/feedback';
import { getRole, setRolePermissions } from '../../services/users/rolesApi';
import {
   listPermissions,
   catalogSupportsSensitivity,
   setPermissionSensitivity,
} from '../../services/users/permissionsApi';
import { listUsers } from '../../services/users/usersApi';
import { TabButton } from '../../styles/ui/Button';

import ModuleRail from './components/ModuleRail';
import PermissionsMatrix from './components/PermissionsMatrix';
import RailSummary from './components/RailSummary';
import ChangesModal from './components/ChangesModal';
import SaveBar from './components/SaveBar';
import UsersTab from './components/UsersTab';
import CreateRoleForm from './components/CreateRoleForm';
import {
   buildCatalogView,
   catalogSummary,
   computeDiff,
   grantedSensitive,
   moduleHasMatch,
   toggleColumn,
   togglePermission,
} from './permissionModel';
import * as S from './styles';

// ---------------------------------------------------------------------------
// Gerenciar perfil de acesso — redesenho 2026-09-15
// (handoff de design: docs/design/design_handoff_permissoes_perfil).
//
// Estado central: `granted` (edição) × `base` (último salvo). Todo o resto —
// contadores do rail, pills, painel ALTERAÇÕES, barra fixa — é derivado.
// Salvar = substituição total via PUT /roles/:id/permissions; "Descartar"
// volta ao baseline sem tocar a API.
//
// Curadoria de sensibilidade (contrato 0111, front-first): só aparece quando
// o backend já expõe `is_sensitive` E o operador tem `iam:permission:update`.
// ---------------------------------------------------------------------------

export default function RoleShow() {
   const { id } = useParams();
   const navigate = useNavigate();
   const { hasPermission } = usePermissions();
   const isCreateMode = !id;

   const [role, setRole] = useState(null);
   const [permissions, setPermissions] = useState([]);
   const [granted, setGranted] = useState(new Set());
   const [base, setBase] = useState(new Set());
   const [selectedModule, setSelectedModule] = useState(null);
   const [query, setQuery] = useState('');
   const [tab, setTab] = useState('perm');
   const [usersQuery, setUsersQuery] = useState('');
   const [usersState, setUsersState] = useState({
      list: [],
      total: 0,
      loading: true,
      forbidden: false,
   });
   const [isLoading, setIsLoading] = useState(!isCreateMode);
   const [isSaving, setIsSaving] = useState(false);
   const [curationOn, setCurationOn] = useState(false);
   const [sensitivityBusyId, setSensitivityBusyId] = useState(null);
   const [diffOpen, setDiffOpen] = useState(false);

   // ─── Carga: papel + catálogo ──────────────────────────────
   useEffect(() => {
      if (isCreateMode) return undefined;
      let alive = true;
      async function load() {
         setIsLoading(true);
         try {
            const [roleData, perms] = await Promise.all([
               getRole(id),
               listPermissions(),
            ]);
            if (!alive) return;
            setRole(roleData);
            setPermissions(perms);
            const initial = new Set(roleData.permissionIds);
            setGranted(initial);
            setBase(new Set(initial));
         } catch (err) {
            notifyApiError(err);
            if (err?.status === 404) navigate('/roles');
         } finally {
            if (alive) setIsLoading(false);
         }
      }
      load();
      return () => {
         alive = false;
      };
   }, [id, isCreateMode, navigate]);

   // ─── Carga: usuários vinculados (degrada em 403 — iam:user:read é
   // Master-only e este widget não pode arrastar a tela pro /403) ──
   useEffect(() => {
      if (isCreateMode) return undefined;
      let alive = true;
      async function loadUsers() {
         try {
            const { users, total } = await listUsers(
               { roleId: id, status: 'all', perPage: 100 },
               { skipForbiddenRedirect: true }
            );
            if (alive) {
               setUsersState({ list: users, total, loading: false, forbidden: false });
            }
         } catch (err) {
            if (!alive) return;
            if (err?.kind === 'forbidden') {
               setUsersState({ list: [], total: 0, loading: false, forbidden: true });
            } else {
               setUsersState({ list: [], total: 0, loading: false, forbidden: false });
               notifyApiError(err);
            }
         }
      }
      loadUsers();
      return () => {
         alive = false;
      };
   }, [id, isCreateMode]);

   // ─── Derivados ────────────────────────────────────────────
   const catalogView = useMemo(() => buildCatalogView(permissions), [permissions]);

   const permissionsById = useMemo(() => {
      const map = new Map();
      permissions.forEach((perm) => map.set(perm.id, perm));
      return map;
   }, [permissions]);

   useEffect(() => {
      if (!selectedModule && catalogView.length) {
         setSelectedModule(catalogView[0].key);
      }
   }, [catalogView, selectedModule]);

   const currentModule = useMemo(
      () => catalogView.find((mod) => mod.key === selectedModule) ?? null,
      [catalogView, selectedModule]
   );

   const summary = useMemo(
      () => catalogSummary(catalogView, granted),
      [catalogView, granted]
   );
   const changes = useMemo(
      () => computeDiff(base, granted, permissionsById),
      [base, granted, permissionsById]
   );
   const addedPerms = useMemo(
      () => changes.filter((c) => c.type === 'added').map((c) => c.perm),
      [changes]
   );
   const removedPerms = useMemo(
      () => changes.filter((c) => c.type === 'removed').map((c) => c.perm),
      [changes]
   );
   const sensitiveList = useMemo(
      () => grantedSensitive(permissions, granted),
      [permissions, granted]
   );
   const globalEmpty = useMemo(
      () =>
         Boolean(query.trim()) &&
         !catalogView.some((mod) => moduleHasMatch(mod, query)),
      [catalogView, query]
   );

   const supportsSensitivity = useMemo(
      () => catalogSupportsSensitivity(permissions),
      [permissions]
   );
   const canCurate = supportsSensitivity && hasPermission('iam:permission:update');

   // ─── Guarda de saída com diff pendente (F5/fechar aba). Bloqueio de
   // navegação interna exigiria data router (useBlocker) — fora por ora. ──
   useEffect(() => {
      if (changes.length === 0) return undefined;
      const handler = (event) => {
         event.preventDefault();
         // eslint-disable-next-line no-param-reassign
         event.returnValue = '';
      };
      window.addEventListener('beforeunload', handler);
      return () => window.removeEventListener('beforeunload', handler);
   }, [changes.length]);

   // ─── Handlers ─────────────────────────────────────────────
   const handleTogglePermission = (resource, perm) => {
      setGranted((prev) => togglePermission(prev, resource, perm));
   };

   const handleToggleColumn = (action) => {
      if (!currentModule) return;
      setGranted((prev) => toggleColumn(prev, currentModule, action));
   };

   const handleDiscard = () => setGranted(new Set(base));

   const handleSave = async () => {
      setIsSaving(true);
      try {
         // O PUT devolve o role completo (0111 §3.7) — o estado local troca
         // pela resposta, sem refetch. Pré-deploy o retorno é null e o
         // conjunto enviado vira o baseline.
         const updated = await setRolePermissions(id, Array.from(granted));
         const nextIds = updated?.permissionIds ?? Array.from(granted);
         setGranted(new Set(nextIds));
         setBase(new Set(nextIds));
         toast.success('Permissões salvas.');
      } catch (err) {
         notifyApiError(err);
      } finally {
         setIsSaving(false);
      }
   };

   // Curadoria: muda o METADADO is_sensitive (não-otimista — aplica só
   // depois da resposta, contrato 0111).
   const handleToggleSensitivity = async (perm) => {
      setSensitivityBusyId(perm.id);
      try {
         const updated = await setPermissionSensitivity(perm.id, !perm.isSensitive);
         const nextValue = updated ? updated.isSensitive : !perm.isSensitive;
         setPermissions((prev) =>
            prev.map((p) => (p.id === perm.id ? { ...p, isSensitive: nextValue } : p))
         );
      } catch (err) {
         notifyApiError(err);
      } finally {
         setSensitivityBusyId(null);
      }
   };

   // ─── Modo criação ─────────────────────────────────────────
   if (isCreateMode) {
      return (
         <S.Wrap>
            <S.DarkTitle>Novo perfil de acesso</S.DarkTitle>
            <CreateRoleForm
               onCancel={() => navigate('/roles')}
               onCreated={(newId) => navigate(`/roles/${newId}`, { replace: true })}
            />
         </S.Wrap>
      );
   }

   const metaParts = [
      `${summary.granted} de ${summary.total} permissões`,
      `${sensitiveList.length} sensíveis`,
   ];
   if (!usersState.forbidden && !usersState.loading) {
      metaParts.push(
         `${usersState.total} ${
            usersState.total === 1 ? 'usuário vinculado' : 'usuários vinculados'
         }`
      );
   }

   return (
      <S.Wrap>
         <Loading isLoading={isLoading || isSaving} />
         <S.DarkTitle>Gerenciar perfil de acesso</S.DarkTitle>

         {role && (
            <S.Card>
               <S.ProfileHead>
                  <S.ProfileHeadLeft>
                     <S.ProfileNameRow>
                        <S.ProfileName>{role.name}</S.ProfileName>
                        <S.IdPill>ID {role.id}</S.IdPill>
                     </S.ProfileNameRow>
                     {role.description ? (
                        <S.ProfileDescription>
                           {role.description}
                        </S.ProfileDescription>
                     ) : null}
                  </S.ProfileHeadLeft>
                  <S.ProfileMetaLine>{metaParts.join(' · ')}</S.ProfileMetaLine>
               </S.ProfileHead>

               <S.TabsRow>
                  <TabButton
                     type="button"
                     $active={tab === 'perm'}
                     onClick={() => setTab('perm')}
                  >
                     Permissões
                  </TabButton>
                  <TabButton
                     type="button"
                     $active={tab === 'users'}
                     onClick={() => setTab('users')}
                  >
                     {usersState.forbidden
                        ? 'Usuários'
                        : `Usuários (${usersState.total})`}
                  </TabButton>
               </S.TabsRow>

               {tab === 'perm' ? (
                  <>
                     <S.SearchToolbar>
                        <S.SearchBox>
                           <FaSearch />
                           <S.SearchInput
                              type="text"
                              value={query}
                              onChange={(e) => setQuery(e.target.value)}
                              placeholder="Buscar permissão, tela ou slug"
                           />
                        </S.SearchBox>
                        <S.ToolbarSpacer />
                        {canCurate && (
                           <S.CurationToggle
                              type="button"
                              $active={curationOn}
                              onClick={() => setCurationOn((prev) => !prev)}
                              title="Alternar o modo de curadoria: marcar/desmarcar permissões como sensíveis"
                           >
                              <FaExclamationTriangle />
                              Curadoria de sensíveis
                           </S.CurationToggle>
                        )}
                     </S.SearchToolbar>

                     {curationOn && (
                        <S.CurationBanner>
                           <FaExclamationTriangle />
                           Modo curadoria: o clique numa permissão marca/desmarca
                           a sensibilidade dela no catálogo (vale para TODOS os
                           perfis) — não altera as concessões deste perfil.
                        </S.CurationBanner>
                     )}

                     <S.PermissionsLayout>
                        <S.RailColumn>
                           <RailSummary
                              summary={summary}
                              sensitiveCount={sensitiveList.length}
                              addedCount={addedPerms.length}
                              removedCount={removedPerms.length}
                              onOpenDiff={() => setDiffOpen(true)}
                           />
                           <ModuleRail
                              modules={catalogView}
                              granted={granted}
                              selected={selectedModule}
                              onSelect={setSelectedModule}
                              query={query}
                           />
                        </S.RailColumn>
                        <PermissionsMatrix
                           mod={currentModule}
                           granted={granted}
                           base={base}
                           query={query}
                           globalEmpty={globalEmpty}
                           onTogglePermission={handleTogglePermission}
                           onToggleColumn={handleToggleColumn}
                           curation={{
                              enabled: curationOn,
                              busyId: sensitivityBusyId,
                              onToggle: handleToggleSensitivity,
                           }}
                        />
                     </S.PermissionsLayout>
                  </>
               ) : (
                  <UsersTab
                     users={usersState.list}
                     total={usersState.total}
                     loading={usersState.loading}
                     forbidden={usersState.forbidden}
                     query={usersQuery}
                     onQueryChange={setUsersQuery}
                     onOpenUser={(userId) => navigate(`/users/${userId}`)}
                  />
               )}
            </S.Card>
         )}

         <SaveBar
            count={changes.length}
            saving={isSaving}
            onDiscard={handleDiscard}
            onSave={handleSave}
            onViewChanges={() => setDiffOpen(true)}
         />

         <ChangesModal
            isOpen={diffOpen}
            onClose={() => setDiffOpen(false)}
            added={addedPerms}
            removed={removedPerms}
         />
      </S.Wrap>
   );
}
